import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QButton, QModalHeader, useCurrentUser, useToastProvider } from '@qualio/ui-components';

import { AxiosError } from 'axios';
import { Approver, Form, FormStatusEnum, Group, QualityEvent, User } from '../../../../../types';
import { QLookupModalComposedHeader } from './QLookupModalComposedHeader';
import approversApi from '../../../../../api/approvers.api';
import { mapToUniversalUserSelectionType } from './utils';
import { useQueryClient } from 'react-query';
import { QLookUpUserWrapper } from '../../../../../components';
import {
  ManageApprovalUsersConfirmationModal,
  ManageApprovalUsersConfirmationModalProps,
} from './ManageApprovalUsersButtonModal/ManageApprovalUsersConfirmationModal';
import {
  computeHasNoPendingApproversLeft,
  computeRequiredApproverSelections,
} from '../../../../../utils/approverUtils';

export type ManageApprovalUsersButtonProps = {
  stepId: number;
  companyId: number;
  groups: Group[];
  users: User[];
  currentApprovalUsers: Approver[];
  allCurrentApprovers?: Approver[];
  qualityApprovalRequired?: boolean;
  qualityEvent: QualityEvent;
  form: Form;
  approverType: 'quality' | 'other' | 'reviewer' | 'mergedApprovers';
};

export const ManageApprovalUsersButton = ({
  stepId,
  companyId,
  qualityEvent,
  groups,
  users,
  form,
  currentApprovalUsers,
  allCurrentApprovers,
  qualityApprovalRequired,
  approverType,
}: ManageApprovalUsersButtonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const currentUniversalApprovalUserSelections = currentApprovalUsers.map(mapToUniversalUserSelectionType);
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const [isLookupModalOpen, setIsLookupModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalProps, setConfirmationModalProps] =
    useState<
      Pick<
        ManageApprovalUsersConfirmationModalProps,
        'addedApprovalUsers' | 'removedApprovalUsers' | 'selections' | 'onSubmit' | 'requiresComment'
      >
    >();

  const onClose = useCallback(() => {
    setIsLookupModalOpen(false);
    setIsConfirmationModalOpen(false);
    setConfirmationModalProps(undefined);
  }, []);

  const isReviewers = useMemo(() => approverType === 'reviewer', [approverType]);
  const approverActor = useMemo(() => (isReviewers ? 'reviewers' : 'approvers'), [isReviewers]);

  const approversTypeMap = useMemo(() => {
    return {
      reviewer: 'reviewers',
      quality: 'quality-approvers',
      other: 'other-approvers',
      mergedApprovers: 'approvers',
    };
  }, []);

  const titleMap = {
    reviewer: t('events.messages.manageReviewers'),
    quality: t('events.messages.manageQualityApprovers'),
    other: t('events.messages.manageOtherApprovers'),
    mergedApprovers: t('events.messages.manageApprovers'),
  };

  const subTitleMap = {
    reviewer: t('events.messages.selectUsersForReviewerFeedback'),
    quality: t('events.messages.selectUsersForQualityApproval'),
    other: t('events.messages.selectUsersForOtherApproval'),
    mergedApprovers: t('events.messages.selectUsersForApproval'),
  };

  const commonToastProps = useMemo(() => {
    return {
      id: `manage-${approversTypeMap[approverType]}-property-toast`,
      replace: true,
    };
  }, [approverType, approversTypeMap]);

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries(['reviewersList', companyId, form.id]);
    queryClient.invalidateQueries(['approversList', companyId, form.id]);
    queryClient.invalidateQueries(['formDetails', form.id]);
    showToast({
      ...commonToastProps,
      status: 'success',
      title: isReviewers ? t('common.labels.reviewersAssigned') : t('common.labels.approversAssigned'),
      description: `Successfully changed the ${approverActor} of the event form.`,
      'data-cy': isReviewers ? 'qe-reviewers-assigned-success-toast' : 'qe-approvers-assigned-success-toast',
    });
    onClose();
  }, [commonToastProps, onClose, showToast, companyId, form.id, queryClient, approverActor, isReviewers, t]);

  const onError = useCallback(
    (error?: string) => {
      const message = error
        ? `Failed to apply changes to ${approverActor}. ${error}`
        : `Failed to apply changes to ${approverActor}. Please try again.`;

      showToast({
        ...commonToastProps,
        status: 'error',
        title: t('common.notifications.error'),
        description: message,
      });
    },
    [commonToastProps, showToast, approverActor, t],
  );

  const currentApprovalUsersMap: Map<number, Approver> = useMemo(
    () =>
      currentApprovalUsers.reduce((acc: Map<Approver['usercompany_id'], Approver>, ApprovalUser) => {
        return acc.set(ApprovalUser.usercompany_id, ApprovalUser);
      }, new Map()),
    [currentApprovalUsers],
  );

  const isCurrentApprovalUser = useCallback(
    (user: Pick<User, 'usercompany_id'>) => currentApprovalUsersMap.has(user.usercompany_id!),
    [currentApprovalUsersMap],
  );

  const selectedIds = useMemo(
    () =>
      confirmationModalProps && new Set(confirmationModalProps.selections.map(({ usercompany_id }) => usercompany_id)),
    [confirmationModalProps],
  );

  const isSelectedOnOpen = useCallback(
    (user: User) => (selectedIds ? selectedIds.has(user.usercompany_id) : isCurrentApprovalUser(user)),
    [selectedIds, isCurrentApprovalUser],
  );

  const getApprovalUserChanges = useCallback(
    (selections: readonly User[]) => {
      const selectedApprovalUserIds = selections.map(mapToUniversalUserSelectionType);
      const addedApprovalUsers = selections
        .map(mapToUniversalUserSelectionType)
        .filter((user) => !isCurrentApprovalUser(user));

      const removedApprovalUsers = currentUniversalApprovalUserSelections.filter(
        (oldItem) => !selectedApprovalUserIds.some((newItem) => newItem.usercompany_id === oldItem.usercompany_id),
      );
      return {
        addedApprovalUsers,
        removedApprovalUsers,
        selectedApprovalUserIds,
      };
    },
    [currentUniversalApprovalUserSelections, isCurrentApprovalUser],
  );

  const applyApprovalUserChanges = useCallback(
    async (selections: readonly User[], comment: string) => {
      const { addedApprovalUsers, removedApprovalUsers } = getApprovalUserChanges(selections);
      try {
        await approversApi.updateApprovers(
          currentUser.companyId,
          qualityEvent.id,
          stepId,
          form.id,
          removedApprovalUsers.map(({ usercompany_id }) => usercompany_id!),
          addedApprovalUsers.map(({ usercompany_id }) => usercompany_id!),
          approverActor,
          comment,
        );
        onSuccess();
      } catch (error) {
        if (error instanceof AxiosError) {
          onError(error.response?.data.message);
        } else {
          onError();
        }
      }
    },
    [currentUser, getApprovalUserChanges, onError, onSuccess, qualityEvent.id, form.id, stepId, approverActor],
  );

  const updateApprovalUsers = useCallback(
    async (selections: readonly User[]) => {
      const { addedApprovalUsers, removedApprovalUsers } = getApprovalUserChanges(selections);

      setConfirmationModalProps({
        addedApprovalUsers,
        removedApprovalUsers,
        selections,
        requiresComment: form.status !== FormStatusEnum.Draft && removedApprovalUsers.length > 0,
        onSubmit: (selections, comment) => applyApprovalUserChanges(selections, comment),
      });

      setIsLookupModalOpen(false);
      setIsConfirmationModalOpen(true);
    },
    [
      applyApprovalUserChanges,
      getApprovalUserChanges,
      form.status,
      setIsLookupModalOpen,
      setIsConfirmationModalOpen,
      setConfirmationModalProps,
    ],
  );

  const showModal = isLookupModalOpen || isConfirmationModalOpen;

  const isApprovalUserDisabled = useCallback(
    (approvalUser: User) => {
      const currentApprovalUser = currentApprovalUsersMap.get(approvalUser.usercompany_id!);
      return currentApprovalUser?.action_status === 'approved' || currentApprovalUser?.action_status === 'declined';
    },
    [currentApprovalUsersMap],
  );

  const showManageButton = useCallback(() => {
    if (!currentUser.permissions['can_work_on_issue']) {
      return false;
    }
    if (currentUser.userId !== form.owner_id && !currentUser.userGroups.includes('quality')) {
      return false;
    }
    if (!['draft', 'for_review', 'for_approval'].includes(form.status)) {
      return false;
    }
    if (approverType === 'reviewer' && form.status === 'for_approval') {
      return false;
    }

    return !(['draft'].includes(form.status) && currentUser.userId !== form.owner_id);
  }, [approverType, currentUser, form]);

  const pendingCurrentApproversCount = useMemo(
    () => currentApprovalUsers.filter((approver) => approver.action_status === 'pending').length,
    [currentApprovalUsers],
  );

  const pendingOtherTypeApproversCount = useMemo(
    () =>
      (allCurrentApprovers ?? [])
        .filter((a) => !currentApprovalUsers.some((ca) => ca.usercompany_id === a.usercompany_id))
        .filter((a) => a.action_status === 'pending').length,
    [allCurrentApprovers, currentApprovalUsers],
  );

  const hasNoPendingApproversLeft = useMemo(
    () =>
      computeHasNoPendingApproversLeft({
        removedApproversCount: confirmationModalProps?.removedApprovalUsers?.length ?? 0,
        addedApproversCount: confirmationModalProps?.addedApprovalUsers?.length ?? 0,
        pendingCurrentApproversCount,
        hasCurrentApprovers: Boolean(currentApprovalUsers.length),
        formStatus: form.status,
        pendingOtherTypeApproversCount,
      }),
    [
      confirmationModalProps?.removedApprovalUsers?.length,
      confirmationModalProps?.addedApprovalUsers?.length,
      pendingCurrentApproversCount,
      currentApprovalUsers.length,
      form.status,
      pendingOtherTypeApproversCount,
    ],
  );

  const requiredSelections = useMemo(
    () =>
      computeRequiredApproverSelections({
        formStatus: form.status,
        approverType,
        qualityApprovalRequired,
        allCurrentApproversCount: (allCurrentApprovers ?? []).length,
        currentApprovalUsersCount: currentApprovalUsers.length,
      }),
    [form.status, approverType, qualityApprovalRequired, allCurrentApprovers, currentApprovalUsers.length],
  );

  return (
    <React.Fragment>
      {showManageButton() && (
        <QButton
          data-cy={`event-form-manage-${approversTypeMap[approverType]}-button`}
          variant="ghost"
          onClick={() => setIsLookupModalOpen(true)}
        >
          Manage
        </QButton>
      )}
      {showModal && (
        <React.Fragment>
          {isLookupModalOpen && (
            <QLookUpUserWrapper
              groups={groups}
              data={users}
              isItemDisabled={isApprovalUserDisabled}
              isItemPreSelected={isCurrentApprovalUser}
              isItemSelectedOnOpen={isSelectedOnOpen}
              handleOnSelect={updateApprovalUsers}
              onClose={onClose}
              isOpen
              requiredSelections={requiredSelections}
            >
              <QModalHeader>
                <QLookupModalComposedHeader title={titleMap[approverType]} subtitle={subTitleMap[approverType]} />
              </QModalHeader>
            </QLookUpUserWrapper>
          )}
          <ManageApprovalUsersConfirmationModal
            {...confirmationModalProps!}
            isReviewers={isReviewers}
            isOpen={isConfirmationModalOpen}
            onClose={onClose}
            onPrevious={() => {
              setIsLookupModalOpen(true);
              setIsConfirmationModalOpen(false);
            }}
            modalTitle={titleMap[approverType]}
            displayWarning={hasNoPendingApproversLeft}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
